import React, { useMemo } from "react";
import SectionHeader from "./SectionHeader";
import EmployeeTooltip from "./EmployeeTooltip";
import {
  formatPercent,
  getTurnoverStatus,
  TURNOVER_RISK_THRESHOLD,
} from "./utils";

const DEFAULT_AXIS_MAX = 15;

const turnoverIcon = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M7 14l4-4 3 3 5-6" />
  </svg>
);

const getDotColor = (status) => (status === "risk" ? "#ef4444" : "#22c55e");

const getAxisMax = (items) => {
  const maxPercent = Math.max(
    ...items.map((item) => item.turnoverPercent || 0),
    TURNOVER_RISK_THRESHOLD,
  );
  if (maxPercent <= DEFAULT_AXIS_MAX) return DEFAULT_AXIS_MAX;
  if (maxPercent <= 25) return 25;
  if (maxPercent <= 50) return 50;
  if (maxPercent <= 75) return 75;
  return Math.ceil(maxPercent / 25) * 25;
};

const getAxisTicks = (axisMax) => {
  if (axisMax <= 15) return [0, 5, 10, 15];
  if (axisMax <= 25) return [0, 5, 10, 15, 20, 25];
  if (axisMax <= 50) return [0, 10, 20, 30, 40, 50];
  if (axisMax <= 75) return [0, 25, 50, 75];
  return [0, 25, 50, 75, 100].filter((tick) => tick <= axisMax);
};

function TurnoverSection({ turnover }) {
  const { items } = turnover;

  const axisMax = useMemo(() => getAxisMax(items), [items]);
  const axisTicks = useMemo(() => getAxisTicks(axisMax), [axisMax]);
  const riskZoneLeft = (TURNOVER_RISK_THRESHOLD / axisMax) * 100;

  return (
    <section className="hr-dashboard__card hr-dashboard__card--turnover">
      <SectionHeader
        icon={turnoverIcon}
        iconTone="orange"
        title="Текучесть персонала"
        subtitle="Увольнения за период относительно средней численности"
      />

      <div className="hr-dashboard__split">
        <div className="hr-dashboard__chart-panel">
          {items.length === 0 ? (
            <p className="hr-dashboard__empty-state">Нет данных по текучести</p>
          ) : (
            <div className="hr-dashboard__lollipop-chart">
              <div
                className="hr-dashboard__lollipop-rows"
                style={{ "--risk-start": `${riskZoneLeft}%` }}
              >
                {items.map((item) => {
                  const percent = item.turnoverPercent || 0;
                  const status = getTurnoverStatus(percent);
                  const position = Math.min((percent / axisMax) * 100, 100);
                  const color = getDotColor(status);
                  const label = item.code || item.name;

                  return (
                    <div key={item.id ?? item.code} className="hr-dashboard__lollipop-row">
                      <div
                        className="hr-dashboard__lollipop-label"
                        title={item.name || item.code}
                      >
                        {label}
                      </div>
                      <div className="hr-dashboard__lollipop-track">
                        <div
                          className="hr-dashboard__lollipop-line"
                          style={{
                            width: `${position}%`,
                            backgroundColor: color,
                          }}
                        />
                        <div
                          className={`hr-dashboard__lollipop-dot hr-dashboard__lollipop-dot--${status}`}
                          style={{
                            left: `${position}%`,
                            borderColor: color,
                            color,
                          }}
                        />
                        <span
                          className="hr-dashboard__lollipop-value"
                          style={{
                            left: `calc(${position}% + 14px)`,
                            color,
                          }}
                        >
                          {formatPercent(percent)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hr-dashboard__lollipop-footer">
                <div className="hr-dashboard__lollipop-axis">
                  {axisTicks.map((tick) => (
                    <span
                      key={tick}
                      style={{ left: `${(tick / axisMax) * 100}%` }}
                    >
                      {tick}%
                    </span>
                  ))}
                </div>
                <div className="hr-dashboard__lollipop-zones">
                  <span
                    className="hr-dashboard__lollipop-zone hr-dashboard__lollipop-zone--norm"
                    style={{ width: `${riskZoneLeft}%` }}
                  >
                    Норма
                  </span>
                  <span
                    className="hr-dashboard__lollipop-zone hr-dashboard__lollipop-zone--risk"
                    style={{ width: `${100 - riskZoneLeft}%` }}
                  >
                    Риск
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="hr-dashboard__table-wrap hr-dashboard__table-wrap--turnover">
          <table className="hr-dashboard__table hr-dashboard__table--turnover">
            <thead>
              <tr>
                <th>Отделение</th>
                <th>Уволено</th>
                <th>Текучесть</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="hr-dashboard__empty-value">
                    Нет данных
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const status = getTurnoverStatus(item.turnoverPercent);
                  const statusLabel = status === "risk" ? "Риск" : "Норма";

                  return (
                    <tr key={item.id ?? item.code}>
                      <td>
                        <span
                          className="hr-dashboard__dept-name-cell"
                          title={item.name}
                        >
                          {item.name || item.code}
                        </span>
                      </td>
                      <td className="hr-dashboard__cell--num">
                        {item.resigned > 0 ? (
                          <EmployeeTooltip
                            title={`Уволено — ${item.name}`}
                            employees={item.dismissedEmployees}
                            emptyText="Список уволенных недоступен"
                          >
                            <span className="hr-dashboard__cell-value">
                              {item.resigned}
                            </span>
                          </EmployeeTooltip>
                        ) : (
                          item.resigned
                        )}
                      </td>
                      <td
                        className={`hr-dashboard__cell--num hr-dashboard__turnover-percent hr-dashboard__turnover-percent--${status}`}
                      >
                        {formatPercent(item.turnoverPercent)}
                      </td>
                      <td>
                        <span
                          className={`hr-dashboard__badge hr-dashboard__badge--${
                            status === "risk" ? "red" : "green"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default TurnoverSection;
